/*#################################################################################################################

File Name           :    SocialSecurityNumber/index.js
Component Name      :    SocialSecurityNumber
Functionality       :    To use this component to validate and get the SSN in the correct format from the user.

#################################################################################################################*/
import { FormLabel, Grid, TextField, IconButton, InputAdornment } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { makeStyles } from '@mui/styles'
import dynamicStyles from '../OutlineStyles'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

const SSNWrapper = ({
  type,
  name,
  label,
  error,
  required,
  value,
  onChange,
  helperText,
  design,
  setError,
  setHelperText,
  placeholder,
  ...otherProps
}) => {
  const [rawSSN, setRawSSN] = useState('')
  const [show, setShow] = useState(false)
  const useStyles = makeStyles((theme) => ({
    textFieldWrapper: {
      width: 'inherit',
      padding: '0 10px',
    },
    formLabelStyle: {
      paddingLeft: '2px',
      paddingBottom: '5px',
    },
  }))

  const borderClasses = dynamicStyles()
  const classes = useStyles()

  const configTextField = {
    classes: {
      root: borderClasses.dynamic,
    },
  }

  const handleChange = (event) => {
    let { value } = event.target
    if (type === 'last4') {
      if (value.length > 4) return
      setRawSSN(value)
      if (onChange) {
        onChange(event)
      }
      return
    }
    if (value.replace(/-/g, '').length < rawSSN.length) {
      const last = rawSSN.length - 1
      const newRawSSN = rawSSN.slice(0, last)
      event.target.value = newRawSSN
      setRawSSN(newRawSSN)
      if (onChange) {
        onChange(event)
      }
      return
    }
    if (!/\d$/.test(value)) return
    let numValue = value.replace(/-/g, '')
    let newSSN = ''
    if (rawSSN.length > 5) {
      newSSN = rawSSN.slice(0, 5) + numValue.slice(5)
    } else {
      newSSN = rawSSN + numValue[numValue.length - 1]
    }
    if (newSSN.length > 9) return
    setRawSSN(newSSN)
    event.target.value = newSSN
    if (onChange) {
      onChange(event)
    }
  }

  const format = (v) => {
    v = v.slice(0, 11).replace(/-/g, '')
    if (v.length <= 3) return v
    if (v.length <= 5) return `${v.slice(0, 3)}-${v.slice(3)}`
    return `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}`
  }

  const mask = (v) => {
    const masked = v.slice(0, 7).replace(/[0-9]/g, '*')
    const final = masked + v.slice(7)
    return final
  }

  return (
    <Grid className={classes.textFieldWrapper}>
      <FormControl fullWidth={true}>
        <FormLabel>{label}</FormLabel>
        <TextField
          className="globelPassword"
          // label={label}
          onChange={handleChange}
          name={name}
          value={type === 'last4' ? rawSSN : show ? format(rawSSN) : mask(format(rawSSN))}
          {...configTextField}
          error={error}
          placeholder={placeholder}
          helperText={helperText}
          variant={design === 'new' ? 'outlined' : 'standard'}
          InputProps={
            type === 'last4'
              ? {}
              : {
                  'data-testid': 'passProps',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle 128d visibility"
                        onClick={() => {
                          setShow(!show)
                        }}
                        data-testid="passButton"
                        className="rem1FSize"
                      >
                        {design === 'new' ? show ? <Visibility /> : <VisibilityOff /> : !show ? 'Hide' : 'Show'}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
          }
        />
      </FormControl>
    </Grid>
  )
}

SSNWrapper.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.string,
  setError: PropTypes.string,
  setHelperText: PropTypes.string,
  placeholder: PropTypes.string,
  mask: PropTypes.string,
  design: PropTypes.string,
}

export default SSNWrapper
